import React from 'react';
import { Typography } from '@mui/material';
import { LineChart } from '@mui/x-charts';
import { format } from 'date-fns';
import { styled } from '@mui/material/styles';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, { accordionSummaryClasses, AccordionSummaryProps } from '@mui/material/AccordionSummary';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { PeriodVolume } from '../../DashboardPage';

interface Props {
  dailyVolume: PeriodVolume[];
  weeklyVolume: PeriodVolume[];
  monthlyVolume: PeriodVolume[];
}

export const ProposalVolumeOverTime = ({ dailyVolume, weeklyVolume, monthlyVolume }: Props) => {
  const [expanded, setExpanded] = React.useState<string | false>('panel1');

  const handleChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  return (
    <>
      <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
        <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
          <Typography component="span">Daily</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {dailyVolume && (
            <LineChart
              height={500}
              dataset={dailyVolume}
              series={[{ label: 'Total', dataKey: 'total' }]}
              xAxis={[
                {
                  scaleType: 'point',
                  dataKey: 'period',
                  label: 'Date',
                  valueFormatter: v => format(new Date(v), 'MM-dd-yyyy'),
                },
              ]}
            />
          )}
        </AccordionDetails>
      </Accordion>
      <Accordion expanded={expanded === 'panel2'} onChange={handleChange('panel2')}>
        <AccordionSummary aria-controls="panel2d-content" id="panel2d-header">
          <Typography component="span">Weekly</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {weeklyVolume && (
            <LineChart
              height={500}
              dataset={weeklyVolume}
              series={[{ label: 'Total', dataKey: 'total' }]}
              xAxis={[
                {
                  scaleType: 'point',
                  dataKey: 'period',
                  label: 'Date',
                  valueFormatter: v => format(new Date(v), 'MM-dd-yyyy'),
                },
              ]}
            />
          )}
        </AccordionDetails>
      </Accordion>
      <Accordion expanded={expanded === 'panel3'} onChange={handleChange('panel3')}>
        <AccordionSummary aria-controls="panel3d-content" id="panel3d-header">
          <Typography component="span">Monthly</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {monthlyVolume && (
            <LineChart
              height={500}
              dataset={monthlyVolume}
              series={[{ label: 'Total', dataKey: 'total' }]}
              xAxis={[
                {
                  scaleType: 'point',
                  dataKey: 'period',
                  label: 'Date',
                  valueFormatter: v => format(new Date(v), 'MM-dd-yyyy'),
                },
              ]}
            />
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(
  ({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&::before': {
      display: 'none',
    },
  })
);

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />} {...props} />
))(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
    transform: 'rotate(90deg)',
  },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
  ...theme.applyStyles('dark', {
    backgroundColor: 'rgba(255, 255, 255, .05)',
  }),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));
